import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { adminApi, parseError, resolveImage } from "./api";

const STATUS_STYLES = { Published: "bg-pale text-[#2F5A0C]", Draft: "bg-mist text-ink", Archived: "bg-line text-ink-muted", Cancelled: "bg-[#FDECEA] text-[#B42318]" };
export const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[status] || "bg-mist"}`} data-testid="admin-status-badge">{status}</span>
);

export const ConfirmButton = ({ label, title, description, onConfirm, disabled, testId, className = "admin-btn-outline" }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button type="button" disabled={disabled} className={className} data-testid={testId}>{label}</button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testId}-cancel`}>Keep as is</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-brand" data-testid={`${testId}-confirm`}>{label}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ImageInput = ({ id, value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await adminApi.post("/uploads", body);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(parseError(e).message);
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload" className="field min-w-0 flex-1" data-testid={`admin-field-${id}`} />
        <button type="button" onClick={() => ref.current.click()} disabled={busy} className="admin-btn-outline inline-flex items-center gap-2" data-testid={`admin-upload-${id}`}>
          <Upload className="h-4 w-4" aria-hidden="true" />{busy ? "Uploading…" : "Upload"}
        </button>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} aria-label="Choose image file" data-testid={`admin-upload-input-${id}`} />
      </div>
      {value && <img src={resolveImage(value)} alt="Selected" className="h-32 w-auto border border-line object-cover" />}
      <p className="text-xs text-ink-muted">JPEG, PNG or WebP, up to 5 MB.</p>
    </div>
  );
};

export const FormField = ({ field, value, onChange, error }) => {
  const id = field.name;
  const desc = error ? `${id}-error` : field.help ? `${id}-help` : undefined;
  const common = { id, "aria-invalid": Boolean(error), "aria-describedby": desc, "data-testid": `admin-field-${id}` };
  const set = (e) => onChange(id, e.target.value);
  let input;
  if (field.type === "checkbox")
    input = (
      <label className="flex items-center gap-3 font-semibold">
        <input type="checkbox" checked={value} onChange={(e) => onChange(id, e.target.checked)} className="h-5 w-5 accent-[#082F6B]" {...common} />
        {field.label}
      </label>
    );
  else if (field.type === "categories")
    input = (
      <div role="group" aria-labelledby={`${id}-label`} aria-describedby={desc} className={`flex max-h-56 flex-wrap gap-2 overflow-y-auto border p-3 ${error ? "border-[#B42318]" : "border-line"}`} data-testid={`admin-field-${id}`}>
        {field.options.length === 0 && <span className="text-sm text-ink-muted">No options yet — add them under Categories.</span>}
        {field.options.map((o) => {
          const on = value.includes(o);
          return (
            <label key={o} className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-sm focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-1 ${on ? "border-brand bg-brand text-white" : "border-line"}`}>
              <input type="checkbox" className="sr-only" checked={on} onChange={() => onChange(id, on ? value.filter((x) => x !== o) : [...value, o])} data-testid={`admin-option-${id}-${o}`} />
              {o}
            </label>
          );
        })}
      </div>
    );
  else if (field.type === "image") input = <ImageInput id={id} value={value} onChange={(v) => onChange(id, v)} />;
  else if (field.type === "select")
    input = (
      <select value={value} onChange={set} className="field" {...common}>
        {!field.noEmpty && <option value="">Not set</option>}
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  else if (["textarea", "markdown", "multi", "speakers"].includes(field.type))
    input = <textarea rows={field.type === "markdown" ? 16 : 4} value={value} onChange={set} className={`field ${field.type === "markdown" ? "font-mono text-sm" : ""}`} {...common} />;
  else
    input = (
      <input
        type={field.type === "datetime" ? "datetime-local" : field.type === "number" ? "number" : field.type === "url" ? "url" : "text"}
        step={field.type === "number" ? "any" : undefined}
        value={value} onChange={set} className="field" placeholder={field.type === "slug" ? "Generated from the title if left blank" : undefined} {...common}
      />
    );
  return (
    <div className="space-y-2">
      {field.type !== "checkbox" && (
        <label htmlFor={id} id={`${id}-label`} className="block text-sm font-bold text-ink">
          {field.label}{field.required && <span className="text-[#B42318]"> *</span>}
        </label>
      )}
      {input}
      {field.type === "multi" && !field.help && <p className="text-xs text-ink-muted">One per line.</p>}
      {field.help && !error && <p id={`${id}-help`} className="text-xs text-ink-muted">{field.help}</p>}
      {error && <p id={`${id}-error`} role="alert" className="text-sm font-semibold text-[#B42318]" data-testid={`admin-error-${id}`}>{error}</p>}
    </div>
  );
};
